"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { ChevronRight } from "lucide-react"
import Script from "next/script"
import { cn } from "@/lib/utils"

export function LandingHero() {
  const canvasRef = useRef<HTMLDivElement>(null)
  const screenshotRef = useRef<HTMLDivElement>(null)
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    // Clean up function to remove the canvas when component unmounts
    return () => {
      if (canvasRef.current) {
        while (canvasRef.current.firstChild) {
          canvasRef.current.removeChild(canvasRef.current.firstChild)
        }
      }
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }
    
    // Add scroll event listener
    window.addEventListener("scroll", handleScroll)
    
    // Call once to set initial position
    handleScroll()
    
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  // List of organizations/businesses that trust CareAdvisor
  const trustedBy = [
    "MN Care Services",
    "Supportive Living",
    "Assisted Living MN",
    "Senior Care Providers",
    "Group Homes",
    "Home Care Agencies",
    "Disability Services",
    "Rehabilitation Centers"
  ]

  // Calculate opacity based on scroll position
  const calculateOpacity = () => {
    // Start at 100px scroll, fully visible by 300px
    const start = 100;
    const end = 300;
    if (scrollY < start) return 0;
    if (scrollY > end) return 1;
    return (scrollY - start) / (end - start);
  }

  const boxOpacity = calculateOpacity();

  return (
    <section className="bg-white dark:bg-[#0F172A] py-14 md:py-20 overflow-hidden relative">
      {/* Main container */}
      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        {/* Main hero content */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
            Minnesota compliance answers <span className="text-[#5BC0F8]">10x</span> faster, just like magic
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-10">
            The most complete regulatory compliance AI suite for Minnesota healthcare providers
          </p>

          <div className="w-full flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="rounded-full relative overflow-hidden text-black bg-[#5BC0F8] hover:bg-[#4bb3ea] transition-all duration-300">
              <Link href="/login">
                <span className="relative z-10 font-medium px-2">Get Answers Now</span>
                <ChevronRight className="ml-1 h-4 w-4 relative z-10" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
              <Link href="#who-its-for">Learn More</Link>
            </Button>
          </div>

        
        </div>

        {/* Demo/screenshot area */}
        <div className="relative mx-auto max-w-4xl" ref={screenshotRef}>
          {/* Main container for the screenshot */}
          <div className="relative rounded-xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] transition-all duration-500">
            {/* Browser-like top bar */}
            <div className="bg-gray-100 dark:bg-gray-800 flex items-center px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="mx-auto bg-white dark:bg-gray-700 rounded-full px-4 py-1 text-xs text-gray-500 dark:text-gray-300 max-w-sm">
                careadvisor.ai
              </div>
            </div>
            
            {/* Screenshot content */}
            <div className="aspect-[16/9] w-full relative">
              {/* Light mode screenshot */}
              <div className="absolute inset-0 dark:opacity-0">
                <Image 
                  src="/assets/careadvisor-screenshot-lightmode.png"
                  alt="CareAdvisor Screenshot Light Mode"
                  fill
                  className="object-cover"
                />
              </div>
              
              {/* Dark mode screenshot */}
              <div className="absolute inset-0 opacity-0 dark:opacity-100">
                <Image 
                  src="/assets/careadvisor-screenshot-darkmode.png"
                  alt="CareAdvisor Screenshot Dark Mode"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </div>
          
          {/* Subtle gradient background instead of strong glow */}
          <div 
            className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 blur-md -z-10"
            style={{ 
              opacity: boxOpacity * 0.7,
              transform: `scale(${1 + (boxOpacity * 0.05)})`,
            }}
          ></div>
        </div>

        {/* Trusted by section with marquee effect */}
        <div className="mt-16">
          <p className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
            TRUSTED BY
          </p>
          
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee whitespace-nowrap">
              {trustedBy.map((org, index) => (
                <div key={index} className="flex items-center justify-center mx-3">
                  <div className="h-10 px-5 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center text-gray-800 dark:text-white font-semibold text-xs">
                    {org}
                  </div>
                </div>
              ))}
              {/* Duplicate for seamless loop */}
              {trustedBy.map((org, index) => (
                <div key={`repeat-${index}`} className="flex items-center justify-center mx-3">
                  <div className="h-10 px-5 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center text-gray-800 dark:text-white font-semibold text-xs">
                    {org}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 